/** Per-section title/rules typography stored in theme as `section_styles` JSON. */

export const SECTION_STYLE_IDS = [
  "submit_your_talent",
  "expolre_and_react",
  "cast_your_vote",
  "game_upload",
];

export const defaultTextStyle = (overrides = {}) => ({
  font_family: "",
  font_size_desktop: 16,
  font_size_mobile: 14,
  color: "#333333",
  ...overrides,
});

export const emptySectionStyles = () => ({
  submit_your_talent: {
    title: defaultTextStyle({ color: "#e25569", font_size_desktop: 20, font_size_mobile: 18 }),
    rules: defaultTextStyle({ color: "#333333" }),
  },
  expolre_and_react: {
    title: defaultTextStyle({ color: "#3b82f6", font_size_desktop: 20, font_size_mobile: 18 }),
    rules: defaultTextStyle({ color: "#333333" }),
  },
  cast_your_vote: {
    title: defaultTextStyle({ color: "#22a06b", font_size_desktop: 20, font_size_mobile: 18 }),
    rules: defaultTextStyle({ color: "#333333" }),
  },
  game_upload: {
    title: defaultTextStyle({ color: "#1a2b6d", font_size_desktop: 18, font_size_mobile: 16 }),
    rules: defaultTextStyle({ color: "#333333" }),
  },
});

function clampSize(value, fallback) {
  if (value === "" || value == null) return fallback;
  const n = Number.parseInt(String(value), 10);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  // Wide range so admin-set values are kept; only reject non-positive / non-numeric.
  return Math.max(1, Math.min(200, n));
}

function normalizePart(raw, fallback) {
  const base = defaultTextStyle(fallback);
  if (!raw || typeof raw !== "object") return base;
  return {
    font_family: raw.font_family != null ? String(raw.font_family) : base.font_family,
    font_size_desktop: clampSize(raw.font_size_desktop, base.font_size_desktop),
    font_size_mobile: clampSize(raw.font_size_mobile, base.font_size_mobile),
    color: raw.color || raw.text_color || base.color,
  };
}

export function parseSectionStyles(raw, legacy = {}) {
  const defaults = emptySectionStyles();
  let parsed = null;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    parsed = raw;
  } else if (typeof raw === "string" && raw.trim()) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }

  const out = emptySectionStyles();
  for (const id of SECTION_STYLE_IDS) {
    const src = parsed?.[id] || {};
    out[id] = {
      title: normalizePart(src.title, defaults[id].title),
      rules: normalizePart(src.rules, defaults[id].rules),
    };
  }

  // Migrate legacy global keys into all sections once if no section_styles yet
  if (!parsed && (legacy.font_family || legacy.rules_text_color || legacy.font_size_desktop)) {
    const legacyRules = normalizePart(
      {
        font_family: legacy.font_family,
        font_size_desktop: legacy.font_size_desktop,
        font_size_mobile: legacy.font_size_mobile,
        color: legacy.rules_text_color || legacy.text_color,
      },
      defaults.submit_your_talent.rules
    );
    for (const id of SECTION_STYLE_IDS) {
      out[id].rules = { ...legacyRules };
    }
  }

  return out;
}

/** Inline style object for player UI (uses desktop size; CSS media can override via vars). */
export function textStyleToCss(style, { isMobile = false } = {}) {
  if (!style) return undefined;
  const size = isMobile ? style.font_size_mobile : style.font_size_desktop;
  const css = {};
  if (style.font_family) css.fontFamily = style.font_family;
  if (size) css.fontSize = `${size}px`;
  if (style.color) css.color = style.color;
  return Object.keys(css).length ? css : undefined;
}

export function ensureFontsFromSectionStyles(sectionStyles, ensureGoogleFont) {
  if (!sectionStyles || !ensureGoogleFont) return;
  for (const id of SECTION_STYLE_IDS) {
    const block = sectionStyles[id];
    if (block?.title?.font_family) ensureGoogleFont(block.title.font_family);
    if (block?.rules?.font_family) ensureGoogleFont(block.rules.font_family);
  }
}
